import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {Card, CardContent, CardHeader, Grid} from '@mui/material';
import Button from '@mui/material/Button';
import {AddOutlined, PlaylistAddCheck} from '@mui/icons-material';
import {useAppDispatch} from '../store';
import {useAppTranslation} from '../services/i18n';
import {selectAuthUser} from 'store/selectors';
import {getFilteredTotal, isApiResultError, type PagedResult} from '../helpers/api';
import {datetimeToGui} from '../helpers/date';
import {decimalNumber} from '../helpers/number';
import {fetchUserApprovalRequests} from 'store/approvals';
import {
    approvalSubjectLabel,
    ApprovalRequestInfo,
    isApprovalCancellable,
    LEARNING_REQUEST_TYPES,
    toApprovalRequestInfo
} from 'model/approval';
import {
    colFilterAny,
    colNoSort,
    createCol,
    DataGrid,
    DataGridCol,
    DataGridColFilter,
    DataGridItemAction,
    DataGridMode,
    DataGridState,
    FilterContext,
    ItemsState,
    OrderDirType
} from 'components/DataGrid';
import {TextFormField} from 'components/form/TextFormField';
import {ApprovalStatusTag} from 'components/approval/ApprovalStatusTag';
import {ApprovalFilterDialog} from 'components/approval/ApprovalFilterDialog';
import {ApprovalCancelDialog} from 'components/approval/ApprovalCancelDialog';
import type {ApprovalFilterType, ApprovalGridFilter} from 'pages/ApprovalsPage';

/** `Pageable::MAX_PAGE_SIZE` -- a larger `per_page` is refused with a 400. */
const MAX_PAGE_SIZE = 1000;

/**
 * The requester's own view of the requests they filed. It reuses the management
 * filter model and dialog, but only the two filters that make sense for one's
 * own records: provider and status.
 *
 * Sorting is limited to what `Users::ApprovalRequests::List` whitelists for
 * learning requests (`price`, `submitted_at`); every other column is explicitly
 * not sortable, and the design only draws an arrow on "Podáno" anyway.
 */
const defaultState: DataGridState<ApprovalRequestInfo, ApprovalGridFilter> = {
    orderCol: 'submitted_at',
    orderDir: OrderDirType.DESC,
    filter: {
        search: '',
        provider: '',
        // no default narrowing: pending, decided and cancelled all belong here
        filterStatuses: [],
    },
    pageSize: 25,
    page: 1
};

export const StudentRequestsPage = () => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const authUser = useSelector(selectAuthUser);

    const [dataGridState, setDataGridState] = useState(defaultState);
    const [itemsState, setItemsState] = useState<ItemsState<ApprovalRequestInfo>>({items: [], isLoading: true});
    const [colFilter, setColFilter] = useState<DataGridColFilter<ApprovalGridFilter, ApprovalFilterType>>();
    const [cancelItem, setCancelItem] = useState<ApprovalRequestInfo>();
    const [refreshKey, setRefreshKey] = useState(0);

    const {filter, orderCol, orderDir, page, pageSize} = dataGridState;

    const queryArgs = useMemo(() => ({
        // course completions have their own screen; this one is requests only
        filterRequestTypes: LEARNING_REQUEST_TYPES,
        filterFulltext: filter.search || undefined,
        filterProviders: filter.provider ? [filter.provider] : undefined,
        filterStatuses: filter.filterStatuses?.length ? filter.filterStatuses : undefined,
        sort: orderCol ? String(orderCol) + ':' + (orderDir || 'asc') : undefined,
    }), [filter, orderCol, orderDir]);

    const handleFetch = useCallback(async () => {
        if (!authUser?.id) {
            return;
        }
        setItemsState(s => ({...s, isLoading: true}));
        const res = await dispatch(fetchUserApprovalRequests({
            ...queryArgs,
            userId: authUser.id,
            page,
            perPage: pageSize
        }));
        if (isApiResultError(res)) {
            setItemsState(s => ({...s, isLoading: false}));
            return;
        }
        const result = res.payload as PagedResult<any>;
        const items = (result.items || []).map(toApprovalRequestInfo);
        setItemsState(s => ({
            items: page && page > 1 ? [...(s.items || []), ...items] : items,
            total: getFilteredTotal(result),
            isLoading: false
        }));
    }, [queryArgs, page, pageSize, authUser?.id, dispatch]);

    useEffect(() => {
        handleFetch().then();
    }, [handleFetch, refreshKey]);

    /**
     * Cancelling only changes one row, so the already loaded pages are kept --
     * but the refetch has to start from page 1 with a page size covering them
     * all, otherwise the incremental branch of `handleFetch` would append the
     * current page a second time and leave the cancelled row stale.
     */
    const handleRefresh = useCallback(() => {
        setDataGridState(s => ({
            ...s,
            page: 1,
            pageSize: Math.min(MAX_PAGE_SIZE,
                Math.max(defaultState.pageSize || 0, itemsState.items.length))
        }));
        setRefreshKey(k => k + 1);
    }, [itemsState.items.length]);

    const handleOpenDetail = useCallback((item: ApprovalRequestInfo) => {
        window.location.href = '#/dashboard/requests/' + item.id;
    }, []);

    const actions: DataGridItemAction<ApprovalRequestInfo>[] = useMemo(() => [
        {title: 'approvals.action.detail', callback: handleOpenDetail},
        // the requester never edits what they filed -- only withdraws it
        {title: 'approvals.action.cancel', callback: setCancelItem, isApplicable: isApprovalCancellable},
    ], [handleOpenDetail]);

    const cols = useMemo(() => {
        const filterContext: FilterContext<ApprovalGridFilter, ApprovalFilterType> = {
            defaultFilter: defaultState.filter,
            currentFilter: dataGridState.filter,
            onOpen: setColFilter,
            onApply: (values) => {
                setDataGridState(s => ({...s, filter: {...s.filter, ...values}, page: 1}));
            }
        };
        const namedFilter = (name: ApprovalFilterType): DataGridColFilter<ApprovalGridFilter, ApprovalFilterType> => ({
            ...filterContext,
            filterName: name,
            isApplied: () => {
                const value = dataGridState.filter[name as keyof ApprovalGridFilter];
                return Array.isArray(value) ? value.length > 0 : !!value;
            }
        });

        const cols: DataGridCol<ApprovalRequestInfo>[] = [
            // a thematic request has no title -- what it carries is the topic
            colNoSort(createCol(t('approvals.table.request'), 'subject_title',
                (v, item) => approvalSubjectLabel(item) || '-', '32%')),
            colFilterAny(
                colNoSort(createCol(t('approvals.table.provider'), 'subject_provider', (v) => v || '-', '20%', 'sm')),
                namedFilter('provider')),
            createCol(t('approvals.table.pricePerStudent'), 'price',
                (v) => (v || v === 0) ? decimalNumber(v, 0) : '-', '14%', 'md'),
            createCol(t('approvals.table.submittedAt'), 'submitted_at', (v) => datetimeToGui(v) || '-', '16%', 'sm'),
            colFilterAny(
                colNoSort(createCol(t('approvals.table.status'), 'status',
                    (v) => <ApprovalStatusTag status={v}/>, '18%')),
                namedFilter('filterStatuses')),
        ];
        return cols;
    }, [t, dataGridState.filter]);

    const createFilter = useCallback((formProps: any) => <Grid container spacing={2}>
        <Grid size={{lg: 4, md: 6, xs: 12}}>
            <TextFormField name={'search'} type={'search'}
                label={t('approvals.filter.search')}
                onBlur={formProps.submitForm}
                maxlength={100}/>
        </Grid>
    </Grid>, [t]);

    return <div className={'layout-fill layout-column tw-absolute'}>
        <div className={'kws-content-margin flex tw-h-100'}>
            <Card className={'tw-h-full tw-flex tw-flex-col'}>
                <CardHeader
                    className={'max-sm:tw-flex-wrap max-sm:tw-gap-12'}
                    title={<>
                        <PlaylistAddCheck/>
                        <span>{t('approvals.requests.title')}</span>
                    </>}
                    action={<Button color={'primary'} variant={'contained'} className={'tw-w-auto'}
                        onClick={() => {
                            window.location.href = '#/dashboard/requests/new';
                        }}>
                        <AddOutlined/>
                        {t('approvals.requests.add')}
                    </Button>}
                />
                <CardContent className={'tw-p-32 tw-pb-0 tw-flex-grow tw-flex tw-flex-col tw-overflow-auto'}>
                    <DataGrid
                        cols={cols}
                        state={dataGridState}
                        defaultState={defaultState}
                        setState={setDataGridState}
                        itemsState={itemsState}
                        mode={DataGridMode.SERVER}
                        createFilter={createFilter}
                        actions={actions}
                        isActionMenu
                        rowClick={handleOpenDetail}
                        emptyListMessage={t('approvals.requests.emptyList')}
                        emptySearchMessage={t('approvals.emptySearch')}
                    />
                    {!!colFilter && <ApprovalFilterDialog colFilter={colFilter} onClose={() => setColFilter(undefined)}/>}
                    {!!cancelItem && !!authUser?.id && <ApprovalCancelDialog
                        item={cancelItem}
                        userId={authUser.id}
                        onSuccess={handleRefresh}
                        onClose={() => setCancelItem(undefined)}/>}
                </CardContent>
            </Card>
        </div>
    </div>;
};
